/**
 * useMapPinPlacement Hook
 *
 * Manages interactive pin placement mode for MapComponent.
 * Handles cursor tracking, keyboard shortcuts, and pin creation.
 *
 * Phase 4: Pin Placement Mode Implementation
 */

import { useState, useCallback, useEffect } from 'react';
import type { MapPin } from '@/components/charts/MapComponent.types';
import { DEFAULT_PIN_STYLE } from '@/components/charts/MapComponent.types';

/**
 * Options for the pin placement hook
 */
export interface UseMapPinPlacementOptions {
  /** Whether pin placement mode is enabled */
  enabled: boolean;
  /** Partial pin data for the pending pin (name, style, etc.) */
  pendingPin?: Partial<MapPin>;
  /** Existing pins array to append to */
  existingPins?: MapPin[];
  /** Callback when a pin is created */
  onPinCreated: (pin: MapPin) => void;
  /** Callback when mode changes */
  onModeChange?: (mode: 'editing' | 'pin-placement') => void;
  /** Callback when placement is cancelled */
  onCancel?: () => void;
}

/**
 * Result from the pin placement hook
 */
export interface UseMapPinPlacementResult {
  /** Whether pin placement mode is active */
  isPlacementMode: boolean;
  /** The pending pin data (if any) */
  pendingPin: Partial<MapPin> | null;
  /** Current cursor position on the map [lng, lat] */
  cursorPosition: [number, number] | null;
  /** Enter pin placement mode with optional defaults */
  enterPlacementMode: (defaults?: Partial<MapPin>) => void;
  /** Exit pin placement mode without creating a pin */
  exitPlacementMode: () => void;
  /** Confirm pin placement at the given coordinates */
  confirmPlacement: (coordinates: [number, number]) => void;
  /** Update cursor position from map mouse events */
  setCursorPosition: (coords: [number, number] | null) => void;
  /** Update pending pin properties */
  updatePendingPin: (updates: Partial<MapPin>) => void;
}

/**
 * Generate a unique pin ID
 */
function generatePinId(): string {
  return `pin-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Hook for managing interactive pin placement on MapComponent
 */
export function useMapPinPlacement(
  options: UseMapPinPlacementOptions
): UseMapPinPlacementResult {
  const {
    enabled,
    pendingPin: initialPendingPin,
    onPinCreated,
    onModeChange,
    onCancel,
  } = options;

  // Local state for placement mode
  const [isPlacementMode, setIsPlacementMode] = useState(enabled);
  const [pendingPin, setPendingPin] = useState<Partial<MapPin> | null>(
    initialPendingPin || null
  );
  const [cursorPosition, setCursorPosition] = useState<[number, number] | null>(null);

  // Sync with external enabled prop
  useEffect(() => {
    setIsPlacementMode(enabled);
    if (enabled && !pendingPin) {
      // Initialize pending pin with defaults when entering placement mode
      setPendingPin({
        name: 'New Pin',
        style: { ...DEFAULT_PIN_STYLE },
        visible: true,
      });
    }
  }, [enabled]);

  // Sync with external pendingPin prop
  useEffect(() => {
    if (initialPendingPin) {
      setPendingPin(prev => ({
        ...prev,
        ...initialPendingPin,
      }));
    }
  }, [initialPendingPin]);

  /**
   * Enter pin placement mode
   */
  const enterPlacementMode = useCallback((defaults?: Partial<MapPin>) => {
    setIsPlacementMode(true);
    setPendingPin({
      name: 'New Pin',
      style: { ...DEFAULT_PIN_STYLE },
      visible: true,
      ...defaults,
    });
    onModeChange?.('pin-placement');
    console.log('[useMapPinPlacement] Entered placement mode');
  }, [onModeChange]);

  /**
   * Exit pin placement mode without creating a pin
   */
  const exitPlacementMode = useCallback(() => {
    setIsPlacementMode(false);
    setPendingPin(null);
    setCursorPosition(null);
    onModeChange?.('editing');
    onCancel?.();
    console.log('[useMapPinPlacement] Exited placement mode');
  }, [onModeChange, onCancel]);

  /**
   * Confirm pin placement at coordinates
   */
  const confirmPlacement = useCallback((coordinates: [number, number]) => {
    if (!isPlacementMode) {
      console.warn('[useMapPinPlacement] confirmPlacement called but not in placement mode');
      return;
    }

    const now = new Date().toISOString();
    const newPin: MapPin = {
      id: generatePinId(),
      name: pendingPin?.name || 'New Pin',
      longitude: coordinates[0],
      latitude: coordinates[1],
      style: pendingPin?.style || { ...DEFAULT_PIN_STYLE },
      description: pendingPin?.description,
      groupId: pendingPin?.groupId,
      navigation: pendingPin?.navigation,
      visible: pendingPin?.visible !== false,
      createdAt: now,
      updatedAt: now,
      metadata: pendingPin?.metadata,
    };

    console.log('[useMapPinPlacement] Pin created:', {
      id: newPin.id,
      name: newPin.name,
      coordinates,
    });

    onPinCreated(newPin);

    // Exit placement mode after creating pin
    setIsPlacementMode(false);
    setPendingPin(null);
    setCursorPosition(null);
    onModeChange?.('editing');
  }, [isPlacementMode, pendingPin, onPinCreated, onModeChange]);

  /**
   * Update pending pin properties
   */
  const updatePendingPin = useCallback((updates: Partial<MapPin>) => {
    setPendingPin(prev => ({
      ...prev,
      ...updates,
      style: updates.style
        ? { ...prev?.style, ...updates.style }
        : prev?.style,
    }));
  }, []);

  // Keyboard event handlers with arrow key fine-tuning
  useEffect(() => {
    if (!isPlacementMode) return;

    // Movement increments (in degrees)
    const SMALL_INCREMENT = 0.0001; // ~11 meters at equator
    const LARGE_INCREMENT = 0.001; // ~111 meters at equator

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          exitPlacementMode();
          break;
        case 'Enter':
          e.preventDefault();
          if (cursorPosition) {
            confirmPlacement(cursorPosition);
          }
          break;
        // Arrow key fine-tuning for accessibility
        case 'ArrowUp':
          e.preventDefault();
          if (cursorPosition) {
            const increment = e.shiftKey ? LARGE_INCREMENT : SMALL_INCREMENT;
            setCursorPosition([cursorPosition[0], cursorPosition[1] + increment]);
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (cursorPosition) {
            const increment = e.shiftKey ? LARGE_INCREMENT : SMALL_INCREMENT;
            setCursorPosition([cursorPosition[0], cursorPosition[1] - increment]);
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (cursorPosition) {
            const increment = e.shiftKey ? LARGE_INCREMENT : SMALL_INCREMENT;
            setCursorPosition([cursorPosition[0] - increment, cursorPosition[1]]);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (cursorPosition) {
            const increment = e.shiftKey ? LARGE_INCREMENT : SMALL_INCREMENT;
            setCursorPosition([cursorPosition[0] + increment, cursorPosition[1]]);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlacementMode, cursorPosition, exitPlacementMode, confirmPlacement]);

  return {
    isPlacementMode,
    pendingPin,
    cursorPosition,
    enterPlacementMode,
    exitPlacementMode,
    confirmPlacement,
    setCursorPosition,
    updatePendingPin,
  };
}

export default useMapPinPlacement;
